import { useState, useEffect } from 'react';
import { toast } from '@/components/ui/sonner';
import { Truck, Plus, History, Calendar, CheckCircle, XCircle, User, ClipboardCheck, AlertTriangle, Wrench, CalendarCheck } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import ParcTab from '@/components/fleet/ParcTab';
import ATraiterTab from '@/components/fleet/ATraiterTab';
import ControlsTab from '@/components/fleet/ControlsTab';
import DailyChecksTab from '@/components/fleet/DailyChecksTab';
import IssuesTab from '@/components/fleet/IssuesTab';
import IssueDetailDialog from '@/components/fleet/IssueDetailDialog';
import DeadlinesTab from '@/components/fleet/DeadlinesTab';
import AccidentsTab, { AccidentDetail } from '@/components/fleet/AccidentsTab';
import DailyHistoryView from '@/components/fleet/DailyHistoryView';
import DriverHistoryView from '@/components/fleet/DriverHistoryView';
import FindDriverSearch from '@/components/fleet/FindDriverSearch';
import { useSearchParams } from 'react-router-dom';

const AdminVehicles = ({ user, activeModule = "b2b" }) => {
  const [activeTab, setActiveTab] = useState('parc');
  const [searchParams, setSearchParams] = useSearchParams();
  const [globalIssueId, setGlobalIssueId] = useState(null);
  const [globalAccidentId, setGlobalAccidentId] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [fleetMe, setFleetMe] = useState(null);
  const [vehicleHistory, setVehicleHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ plate_number: '', brand: '', model: '', site_id: '' });
  const [submitting, setSubmitting] = useState(false);
  const [historyDateFilter, setHistoryDateFilter] = useState('');
  const [historyMode, setHistoryMode] = useState('van');

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { if (activeTab === 'history') fetchVehicleHistory(); }, [activeTab]);

  useEffect(() => {
    const issue = searchParams.get('issue');
    const accident = searchParams.get('accident');
    const tab = searchParams.get('tab');
    if (issue) setGlobalIssueId(issue);
    if (accident) setGlobalAccidentId(accident);
    if (tab === 'deadlines') setActiveTab('deadlines');
    else if (tab === 'daily') setActiveTab('daily');
    else if (searchParams.get('vehicle')) setActiveTab('parc');
  }, [searchParams]);

  useEffect(() => {
    const goTab = (e) => setActiveTab(e.detail);
    window.addEventListener('fleet-goto-tab', goTab);
    return () => window.removeEventListener('fleet-goto-tab', goTab);
  }, []);

  const fetchData = async () => {
    try {
      const [vehiclesRes, employeesRes, sitesRes, meRes] = await Promise.all([
        axios.get(`${API}/fleet/vehicles`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/sites`, { withCredentials: true }),
        axios.get(`${API}/fleet/me`, { withCredentials: true })
      ]);
      setVehicles(vehiclesRes.data);
      setEmployees(employeesRes.data.filter(e => e.role === 'employee'));
      setSites(sitesRes.data.sites || sitesRes.data || []);
      setFleetMe(meRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/photos/admin/history?days=0`, { withCredentials: true });
      const historyData = [];
      response.data.forEach(dayData => {
        Object.entries(dayData.vehicles || {}).forEach(([plate, sessions]) => {
          sessions.forEach(session => {
            if (session.user) {
              historyData.push({
                date: dayData.date,
                vehicle_plate: plate,
                user_name: session.user.name || 'Inconnu',
                user_id: session.user.user_id || session.user_id,
                session_type: session.session_type,
                tour_type: session.tour_type,
                whatsapp_verified: session.whatsapp_photos_verified,
                is_complete: session.is_complete
              });
            }
          });
        });
      });
      historyData.sort((a, b) => new Date(b.date) - new Date(a.date));
      setVehicleHistory(historyData);
    } catch (error) {
      console.error('Failed to fetch vehicle history:', error);
      setVehicleHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleWhatsappValidation = async (userId, userName, date, sessionType, isVerified) => {
    try {
      await axios.put(
        `${API}/photos/session/${userId}/${date}/${sessionType}/whatsapp`,
        { verified: isVerified, notify_if_rejected: !isVerified, user_name: userName },
        { withCredentials: true }
      );
      fetchVehicleHistory();
    } catch (error) {
      console.error('Failed to update WhatsApp status:', error);
      toast.error(`Statut WhatsApp non enregistré : ${typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'vérifiez la connexion puis réessayez'}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/vehicles`, form, { withCredentials: true, timeout: 10000 });
      setShowForm(false);
      setForm({ plate_number: '', brand: '', model: '', site_id: '' });
      fetchData();
    } catch (error) {
      alert(`Erreur: ${error.response?.data?.detail || error.message || 'Erreur inconnue'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredHistory = historyDateFilter
    ? vehicleHistory.filter(h => h.date === historyDateFilter)
    : vehicleHistory;
  const groupedHistory = filteredHistory.reduce((acc, item) => {
    (acc[item.date] = acc[item.date] || []).push(item);
    return acc;
  }, {});

  const tabs = [
    { key: 'parc', label: 'Vue Parc', icon: Truck },
    { key: 'atraiter', label: 'À traiter', icon: Wrench },
    { key: 'daily', label: 'Contrôle quotidien', icon: CalendarCheck },
    { key: 'controls', label: 'Contrôle périodique', icon: ClipboardCheck },
    { key: 'issues', label: 'Anomalies', icon: AlertTriangle },
    { key: 'deadlines', label: 'Échéances', icon: Calendar },
    { key: 'accidents', label: 'Accidents', icon: AlertTriangle },
    { key: 'history', label: 'Historique', icon: History },
  ];

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-6 fade-in">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#0F172A] font-['Chivo']">Suivi VAN</h1>
            <p className="text-gray-500">Parc, contrôles, anomalies et maintenance des véhicules</p>
          </div>
          {activeTab === 'parc' && (
            <Dialog open={showForm} onOpenChange={setShowForm}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="new-vehicle-btn">
                  <Plus className="w-4 h-4 mr-2" /> Nouveau véhicule
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Ajouter un véhicule</DialogTitle></DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>Immatriculation *</Label>
                    <Input value={form.plate_number}
                      onChange={(e) => setForm({ ...form, plate_number: e.target.value.toUpperCase() })}
                      placeholder="AB-123-CD" required data-testid="vehicle-plate-input" />
                  </div>
                  <div>
                    <Label>Marque</Label>
                    <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Renault" />
                  </div>
                  <div>
                    <Label>Modèle</Label>
                    <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="Master" />
                  </div>
                  <div>
                    <Label>Site affecté</Label>
                    <select value={form.site_id || ''} onChange={(e) => setForm({ ...form, site_id: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md bg-white text-sm">
                      <option value="">Aucun (flotte commune)</option>
                      {sites.map(site => <option key={site.site_id} value={site.site_id}>{site.name}</option>)}
                    </select>
                  </div>
                  <Button type="submit" className="w-full" disabled={submitting} data-testid="vehicle-create-submit">
                    {submitting ? 'Enregistrement...' : 'Ajouter'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="flex gap-1 border-b overflow-x-auto">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`px-3 py-2 font-medium text-sm whitespace-nowrap ${activeTab === t.key ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
              data-testid={`fleet-tab-${t.key}`}>
              <t.icon className="w-4 h-4 inline mr-1.5" />{t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F172A]"></div>
          </div>
        ) : (
          <>
            {activeTab === 'parc' && (
              <ParcTab vehicles={vehicles} sites={sites} employees={employees} fleetMe={fleetMe} onRefresh={fetchData} />
            )}
            {activeTab === 'atraiter' && (
              <ATraiterTab vehicles={vehicles} sites={sites} fleetMe={fleetMe} onRefreshParc={fetchData}
                onOpenAccident={(id) => setGlobalAccidentId(id)} />
            )}
            {activeTab === 'daily' && <DailyChecksTab onRefreshParc={fetchData} sites={sites} />}
            {activeTab === 'controls' && <ControlsTab onRefreshParc={fetchData} />}
            {activeTab === 'issues' && (
              <IssuesTab vehicles={vehicles} isFleetManager={fleetMe?.is_fleet_manager} onRefreshParc={fetchData} />
            )}
            {activeTab === 'deadlines' && <DeadlinesTab vehicles={vehicles} sites={sites} onRefreshParc={fetchData} />}
            {activeTab === 'accidents' && <AccidentsTab vehicles={vehicles} sites={sites} />}
          </>
        )}

        <IssueDetailDialog issueId={globalIssueId} open={!!globalIssueId}
          onClose={() => { setGlobalIssueId(null); searchParams.delete('issue'); setSearchParams(searchParams, { replace: true }); }}
          isFleetManager={fleetMe?.is_fleet_manager} onChanged={fetchData} />
        {globalAccidentId && (
          <AccidentDetail accidentId={globalAccidentId} open
            onClose={() => { setGlobalAccidentId(null); searchParams.delete('accident'); setSearchParams(searchParams, { replace: true }); }}
            onChanged={fetchData} />
        )}

        {activeTab === 'history' && (
          <div className="space-y-4">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
              <button type="button" onClick={() => setHistoryMode('van')}
                className={`px-3 py-1.5 rounded text-sm font-medium ${historyMode === 'van' ? 'bg-white shadow text-blue-700' : 'text-gray-500'}`}
                data-testid="history-mode-van-btn">Suivi VAN (photos)</button>
              <button type="button" onClick={() => setHistoryMode('daily')}
                className={`px-3 py-1.5 rounded text-sm font-medium ${historyMode === 'daily' ? 'bg-white shadow text-blue-700' : 'text-gray-500'}`}
                data-testid="history-mode-daily-btn">Contrôles quotidiens</button>
              <button type="button" onClick={() => setHistoryMode('drivers')}
                className={`px-3 py-1.5 rounded text-sm font-medium ${historyMode === 'drivers' ? 'bg-white shadow text-blue-700' : 'text-gray-500'}`}
                data-testid="history-mode-drivers-btn">Conducteurs / Amendes</button>
            </div>
            {historyMode === 'drivers' ? (
              <div className="space-y-4">
                <FindDriverSearch sites={sites} />
                <div>
                  <h3 className="font-semibold text-sm mb-2">Historique des conducteurs</h3>
                  <DriverHistoryView sites={sites} />
                </div>
              </div>
            ) : historyMode === 'daily' ? (
              <DailyHistoryView sites={sites} />
            ) : (
            <>
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <div className="flex-1">
                    <Label>Filtrer par date</Label>
                    <Input type="date" value={historyDateFilter}
                      onChange={(e) => setHistoryDateFilter(e.target.value)} className="mt-1" />
                  </div>
                  {historyDateFilter && (
                    <Button variant="outline" onClick={() => setHistoryDateFilter('')}>Effacer le filtre</Button>
                  )}
                </div>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-800">Validation WhatsApp</p>
                    <p className="text-sm text-green-700">
                      Cliquez sur ✅ ou ❌ pour indiquer si les photos ont été reçues sur WhatsApp.
                      Seuls les véhicules récupérés par un livreur apparaissent ici.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            {loadingHistory ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F172A]"></div>
              </div>
            ) : Object.keys(groupedHistory).length === 0 ? (
              <Card><CardContent className="p-8 text-center">
                <History className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">Aucun historique disponible</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedHistory).map(([date, items]) => (
                  <Card key={date}>
                    <CardHeader className="bg-gray-50 py-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-blue-600" />
                        {new Date(date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                        <span className="text-sm font-normal text-gray-500 ml-2">({items.length} utilisation{items.length > 1 ? 's' : ''})</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        {items.map((entry, idx) => (
                          <div key={`${entry.vehicle_plate}-${entry.user_id}-${entry.session_type}-${idx}`}
                            className={`p-4 rounded-lg border-2 transition-all ${
                              entry.whatsapp_verified === true ? 'bg-green-100 border-green-400'
                                : entry.whatsapp_verified === false ? 'bg-red-100 border-red-400'
                                  : 'bg-amber-50 border-amber-300'}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                    entry.whatsapp_verified === true ? 'bg-green-600'
                                      : entry.whatsapp_verified === false ? 'bg-red-600' : 'bg-blue-600'}`}>
                                    <Truck className="w-5 h-5 text-white" />
                                  </div>
                                  <div>
                                    <p className="font-mono font-bold text-lg">{entry.vehicle_plate}</p>
                                    <p className="text-xs text-gray-500">
                                      {entry.session_type === 'start' ? '🌅 Début' : '🌙 Fin'}
                                      {entry.tour_type && ` - ${entry.tour_type}`}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 ml-4 pl-4 border-l-2 border-gray-300">
                                  <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                                    <User className="w-4 h-4 text-white" />
                                  </div>
                                  <div>
                                    <p className="font-semibold">{entry.user_name}</p>
                                    <p className="text-xs text-gray-500">Livreur</p>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`text-xs px-2 py-1 rounded font-medium ${
                                  entry.whatsapp_verified === true ? 'bg-green-100 text-green-700'
                                    : entry.whatsapp_verified === false ? 'bg-red-100 text-red-700'
                                      : 'bg-yellow-100 text-yellow-700'}`}>
                                  {entry.whatsapp_verified === true ? '✓ Validé'
                                    : entry.whatsapp_verified === false ? '⚠️ Refusé' : '○ Non traité'}
                                </span>
                                <button onClick={() => handleWhatsappValidation(entry.user_id, entry.user_name, date, entry.session_type, true)}
                                  className={`w-9 h-9 rounded flex items-center justify-center transition-all ${
                                    entry.whatsapp_verified === true
                                      ? 'bg-green-600 text-white shadow-md ring-2 ring-green-300'
                                      : 'bg-green-100 text-green-600 hover:bg-green-200 border border-green-300'}`}
                                  title="Valider - Photos WhatsApp reçues ✓">
                                  <CheckCircle className="w-5 h-5" />
                                </button>
                                <button onClick={() => handleWhatsappValidation(entry.user_id, entry.user_name, date, entry.session_type, false)}
                                  className={`w-9 h-9 rounded flex items-center justify-center transition-all ${
                                    entry.whatsapp_verified === false
                                      ? 'bg-red-600 text-white shadow-md ring-2 ring-red-300'
                                      : 'bg-red-100 text-red-600 hover:bg-red-200 border border-red-300'}`}
                                  title="Refuser - Photos non reçues (notifie le salarié)">
                                  <XCircle className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminVehicles;
